import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container, Stack, Title, Text, Paper, Group, Button, Loader, Center, Badge,
  CopyButton, Tooltip, ActionIcon,
} from '@mantine/core'
import { IconArrowLeft, IconCopy, IconCheck, IconUserStar } from '@tabler/icons-react'
import { useTeam } from '../hooks/useTeam'
import { TeamRosterCard } from '../components/teams/TeamRosterCard'
import { TeamCurrentStoryPanel } from '../components/teams/TeamCurrentStoryPanel'
import { TeamStoryHistory } from '../components/teams/TeamStoryHistory'
import { AddStoryModal } from '../components/teams/AddStoryModal'

export default function TeamDashboard() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: team, isLoading, error } = useTeam(id)
  const [addOpen, setAddOpen] = useState(false)

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Center py="xl"><Loader color="cyan" /></Center>
      </Container>
    )
  }

  if (error || !team) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">Team not found</Text>
          <Text c="dimmed" size="sm">{error?.response?.data?.error || 'You may not have access to this team.'}</Text>
          <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/teams')}>
            My Teams
          </Button>
        </Stack>
      </Container>
    )
  }

  const isFacilitator = !!team.is_facilitator
  const myMember = (team.members || []).find((m) => m.is_me)
  const isInactiveMember = myMember?.status === 'inactive'
  const canPlay = !isInactiveMember
  const inviteUrl = `${window.location.origin}/team/${team.invite_code}`
  const excludeStoryIds = [
    ...(team.history || []).map((h) => h.story_id),
    team.current_story?.story_id,
  ].filter(Boolean)

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/teams')}
          >
            My Teams
          </Button>
        </Group>

        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
              {team.name}
            </Title>
            {isFacilitator && (
              <Badge color="cyan" leftSection={<IconUserStar size={10} />} variant="light" size="sm">
                You're the facilitator
              </Badge>
            )}
          </Stack>
        </Group>

        {isFacilitator && (
          <Paper p="lg" className="glass">
            <Stack gap="xs">
              <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" tt="uppercase" lts={1}>
                Invite Link
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="cyan.2" style={{ wordBreak: 'break-all', flex: 1 }}>
                  {inviteUrl}
                </Text>
                <CopyButton value={inviteUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                      <ActionIcon variant="light" color="cyan" onClick={copy}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed">
                Share with members enrolled in the same Group. Provisional accounts can't join.
              </Text>
            </Stack>
          </Paper>
        )}

        <TeamCurrentStoryPanel
          team={team}
          currentStory={team.current_story}
          isFacilitator={isFacilitator}
          isInactiveMember={isInactiveMember}
          canPlay={canPlay}
          onAddNext={() => setAddOpen(true)}
        />

        <TeamRosterCard members={team.members || []} facilitatorView={isFacilitator} />

        <TeamStoryHistory teamId={team.id} history={team.history || []} />

        <AddStoryModal
          teamId={team.id}
          opened={addOpen}
          onClose={() => setAddOpen(false)}
          excludeStoryIds={excludeStoryIds}
        />
      </Stack>
    </Container>
  )
}
